import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, Tabs, Tab } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaEye } from 'react-icons/fa';
import { adsApi, adSetsApi, feedAdsApi } from '../../../services/sponsoredAdsApi';
import { uploadBannerFileViaPresignedUrl } from '../../../services/bannerAdsApi';
import SetAlert from '../../../components/SetAlert';
import {
  FEED_PLACEMENT_KEYS,
  FEED_PLACEMENT_LABELS,
  formatPlacementList
} from '../feedAdPlacements';

const S3_ADS_FOLDER = 'ads/feed';

const Ads = () => {
  const [ads, setAds] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [previewAd, setPreviewAd] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });
  const [previewTab, setPreviewTab] = useState('feed');
  const [placementOptions, setPlacementOptions] = useState(FEED_PLACEMENT_KEYS);
  const [uploadingField, setUploadingField] = useState(null);

  const [formData, setFormData] = useState({
    adSetId: '',
    assetName: '',
    caption: '',
    media: '',
    ctaText: '',
    ctaUrl: '',
    priority: 1,
    mediaType: 'image',
    m3u8Url: '',
    thumbnailUrl: '',
    supportedPlacements: []
  });

  useEffect(() => {
    fetchAds();
    fetchAdSets();
    fetchPlacementOptions();
  }, []);

  const fetchPlacementOptions = async () => {
    try {
      const response = await feedAdsApi.getPlacements();
      if (response.statusCode === '10000' && Array.isArray(response.data?.placements)) {
        setPlacementOptions(response.data.placements);
      }
    } catch (error) {
      console.error('Error fetching feed placements:', error);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const response = await adsApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(ad => ({
              id: ad.id,
              assetName: ad.asset_name,
              caption: ad.caption,
              media: ad.media,
              ctaText: ad.cta_text,
              ctaUrl: ad.cta_url,
              priority: ad.priority,
              adSetId: ad.ad_set_id,
              mediaType: ad.media_type || 'image',
              m3u8Url: ad.m3u8_url || '',
              thumbnailUrl: ad.thumbnail_url || '',
              supportedPlacements: ad.supported_placements || [],
              status: ad.status || 'active',
              createdAt: ad.created_at,
              updatedAt: ad.updated_at
            }))
          : [];
        setAds(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch ads' }));
        setAds([]);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching ads' }));
      setAds([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdSets = async () => {
    try {
      const response = await adSetsApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(adSet => ({
              id: adSet.id,
              adSetName: adSet.ad_set_name,
              brandName: adSet.brand_name,
              brandLogoUrl: adSet.brand_logo_url,
              ctaColor: adSet.cta_color,
              userId: adSet.ad_user_id,
              adUserName: adSet.ad_user_name,
              adUserBrandName: adSet.ad_user_brand_name,
              status: adSet.status || 'active'
            }))
          : [];
        setAdSets(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch ad sets' }));
        setAdSets([]);
      }
    } catch (error) {
      console.error('Error fetching ad sets:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching ad sets' }));
      setAdSets([]);
    }
  };

  const emptyFormData = () => ({
    adSetId: '',
    assetName: '',
    caption: '',
    media: '',
    ctaText: '',
    ctaUrl: '',
    priority: 1,
    mediaType: 'image',
    m3u8Url: '',
    thumbnailUrl: '',
    supportedPlacements: []
  });

  const handleShowModal = (ad = null) => {
    if (ad) {
      setFormData({
        adSetId: ad.adSetId,
        assetName: ad.assetName,
        caption: ad.caption,
        media: ad.media,
        ctaText: ad.ctaText,
        ctaUrl: ad.ctaUrl,
        priority: ad.priority,
        mediaType: ad.mediaType || 'image',
        m3u8Url: ad.m3u8Url || '',
        thumbnailUrl: ad.thumbnailUrl || '',
        supportedPlacements: ad.supportedPlacements || []
      });
      setSelectedAd(ad);
    } else {
      setFormData(emptyFormData());
      setSelectedAd(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAd(null);
    setFormData(emptyFormData());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlacementToggle = (key) => {
    setFormData((prev) => {
      const current = prev.supportedPlacements || [];
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      return { ...prev, supportedPlacements: next };
    });
  };

  const ensureAssetNameForUpload = () => {
    const name = (formData.assetName || '').trim();
    if (!name) {
      setAlertMessage((prev) => ({
        ...prev,
        error: 'Enter an asset name before uploading media.',
        success: ''
      }));
      return null;
    }
    return name.replace(/\s+/g, '_');
  };

  const uploadMediaFile = async (file, fieldKey, subfolder) => {
    const safeName = ensureAssetNameForUpload();
    if (!safeName) return;

    setUploadingField(fieldKey);
    try {
      const ext = file.name.split('.').pop();
      const isVideo = file.type?.startsWith('video/');
      const objectKey = `${S3_ADS_FOLDER}/${safeName}/${subfolder}.${ext}`;
      const location = await uploadBannerFileViaPresignedUrl(file, objectKey);

      setFormData((prev) => {
        const updates = { [fieldKey]: location };
        if (fieldKey === 'media' && isVideo) {
          updates.mediaType = 'video';
          if (!prev.thumbnailUrl) updates.thumbnailUrl = location;
        }
        if (fieldKey === 'media' && !isVideo) {
          updates.mediaType = 'image';
        }
        return { ...prev, ...updates };
      });

      setAlertMessage((prev) => ({
        ...prev,
        success: 'Media uploaded successfully',
        error: ''
      }));
    } catch (error) {
      console.error('Error uploading media:', error);
      setAlertMessage((prev) => ({
        ...prev,
        error: error?.message || 'Failed to upload media',
        success: ''
      }));
    } finally {
      setUploadingField(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await uploadMediaFile(file, 'media', 'media');
    if (e.target) e.target.value = '';
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await uploadMediaFile(file, 'thumbnailUrl', 'thumbnail');
    if (e.target) e.target.value = '';
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await uploadMediaFile(file, 'm3u8Url', 'video');
    setFormData((prev) => ({ ...prev, mediaType: 'video' }));
    if (e.target) e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiData = {
        asset_name: formData.assetName,
        caption: formData.caption,
        media: formData.media,
        cta_text: formData.ctaText,
        cta_url: formData.ctaUrl,
        priority: formData.priority,
        ad_set_id: formData.adSetId,
        media_type: formData.mediaType || 'image'
      };

      if (formData.m3u8Url?.trim()) {
        apiData.m3u8_url = formData.m3u8Url.trim();
      }
      if (formData.thumbnailUrl?.trim()) {
        apiData.thumbnail_url = formData.thumbnailUrl.trim();
      }
      if (formData.supportedPlacements?.length > 0) {
        apiData.supported_placements = formData.supportedPlacements;
      }
      
      const response = await (selectedAd 
        ? adsApi.update(selectedAd.id, apiData)
        : adsApi.create(apiData));

      if (response.statusCode === "10000") {
        setAlertMessage(prev => ({ 
          ...prev, 
          success: response.message || `Ad ${selectedAd ? 'updated' : 'created'} successfully` 
        }));
        handleCloseModal();
        fetchAds();
      } else {
        setAlertMessage(prev => ({ 
          ...prev, 
          error: response.message || `Failed to ${selectedAd ? 'update' : 'create'} ad` 
        }));
      }
    } catch (error) {
      console.error('Error saving ad:', error);
      setAlertMessage(prev => ({ 
        ...prev, 
        error: `Error ${selectedAd ? 'updating' : 'creating'} ad` 
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ad?')) {
      try {
        const response = await adsApi.delete(id);
        if (response.statusCode === "10000") {
          setAlertMessage(prev => ({ ...prev, success: response.message || 'Ad deleted successfully' }));
          fetchAds();
        } else {
          setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to delete ad' }));
        }
      } catch (error) {
        console.error('Error deleting ad:', error);
        setAlertMessage(prev => ({ ...prev, error: 'Error deleting ad' }));
      }
    }
  };

  const handlePreview = (ad) => {
    setPreviewAd(ad);
    setPreviewTab(ad.mediaType === 'video' ? 'reel' : 'feed');
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewAd(null);
  };

  const filteredAds = Array.isArray(ads) ? ads.filter(ad => {
    const matchesSearch = (
      ad.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.caption.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return matchesSearch;
  }) : [];

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-container">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Feed Ads</h4>
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FaPlus className="me-2" />
            New Feed Ad
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={12}>
              <Form.Control
                type="text"
                placeholder="Search ads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Asset Name</th>
                <th>Caption</th>
                <th>Media</th>
                <th>Type</th>
                <th>Placements</th>
                <th>CTA</th>
                <th>Priority</th>
                <th>Ad Set</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAds.map(ad => (
                <tr key={ad.id}>
                  <td>{ad.assetName}</td>
                  <td>{ad.caption}</td>
                  <td>
                    {ad.media && (
                      <img 
                        src={ad.thumbnailUrl || ad.media} 
                        alt={ad.assetName} 
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                      />
                    )}
                  </td>
                  <td>
                    <Badge bg={ad.mediaType === 'video' ? 'info' : 'secondary'}>
                      {ad.mediaType || 'image'}
                    </Badge>
                  </td>
                  <td>
                    <small>{formatPlacementList(ad.supportedPlacements)}</small>
                  </td>
                  <td>
                    <div>{ad.ctaText}</div>
                    <a href={ad.ctaUrl} target="_blank" rel="noopener noreferrer" className="small">
                      {ad.ctaUrl}
                    </a>
                  </td>
                  <td>{ad.priority}</td>
                  <td>
                    {Array.isArray(adSets) && adSets.find(adSet => adSet.id === ad.adSetId)?.adSetName || 'N/A'}
                  </td>
                  <td>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="me-2"
                      onClick={() => handlePreview(ad)}
                    >
                      <FaEye />
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(ad)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(ad.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={handleClosePreview} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Feed Ad Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewAd && (() => {
            const associatedAdSet = adSets.find(adSet => adSet.id === previewAd.adSetId);
            const ctaColor = associatedAdSet?.ctaColor || '#000000';
            const brandName = associatedAdSet?.brandName || 'Brand Name';
            const brandLogoUrl = associatedAdSet?.brandLogoUrl;

            const renderCtaButton = (fullWidth = true) => (
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: `${ctaColor}CC`,
                  borderColor: ctaColor,
                  color: '#ffffff',
                  padding: '12px 16px',
                  width: fullWidth ? '100%' : 'auto',
                  borderRadius: fullWidth ? '0' : '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onClick={() => window.open(previewAd.ctaUrl, '_blank')}
              >
                <span>{previewAd.ctaText}</span>
                <span style={{ fontSize: '18px' }}>→</span>
              </button>
            );

            const renderBrandHeader = () => (
              <div className="ad-preview-header p-3" style={{
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div className="d-flex align-items-center">
                  {brandLogoUrl ? (
                    <img
                      src={brandLogoUrl}
                      alt="Brand Logo"
                      className="me-2"
                      style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="me-2" style={{
                      width: '40px', height: '40px', borderRadius: '50%',
                      backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <span style={{ color: '#6c757d', fontSize: '20px' }}>?</span>
                    </div>
                  )}
                  <h6 className="mb-0">{brandName}</h6>
                </div>
                <small className="text-muted">Sponsored</small>
              </div>
            );

            return (
              <Tabs activeKey={previewTab} onSelect={(k) => setPreviewTab(k)} className="mb-3">
                <Tab eventKey="feed" title="Feed card">
                  <div className="ad-preview" style={{
                    border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden',
                    maxWidth: '500px', margin: '0 auto'
                  }}>
                    {renderBrandHeader()}
                    <div className="ad-preview-content p-3">
                      <p className="mb-0">{previewAd.caption}</p>
                    </div>
                    <div className="ad-preview-media" style={{ position: 'relative' }}>
                      <div style={{ width: '100%', height: '300px', backgroundColor: '#f8f9fa', position: 'relative' }}>
                        <img
                          src={previewAd.thumbnailUrl || previewAd.media}
                          alt={previewAd.assetName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
                          {renderCtaButton(true)}
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="reel" title="Reel (full screen)">
                  <div style={{
                    border: '1px solid #ddd', borderRadius: '12px', overflow: 'hidden',
                    maxWidth: '320px', margin: '0 auto', backgroundColor: '#000', position: 'relative',
                    aspectRatio: '9/16', minHeight: '480px'
                  }}>
                    {previewAd.mediaType === 'video' && previewAd.m3u8Url ? (
                      <video
                        src={previewAd.m3u8Url}
                        poster={previewAd.thumbnailUrl || previewAd.media}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        controls
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        src={previewAd.thumbnailUrl || previewAd.media}
                        alt={previewAd.assetName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                    <div style={{
                      position: 'absolute', top: 12, left: 12, right: 12,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <Badge bg="dark" style={{ opacity: 0.85 }}>Sponsored</Badge>
                    </div>
                    <div style={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      padding: '16px', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', color: '#fff'
                    }}>
                      <div className="d-flex align-items-center mb-2">
                        {brandLogoUrl && (
                          <img src={brandLogoUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8 }} />
                        )}
                        <small>{brandName}</small>
                      </div>
                      <p className="mb-2 small">{previewAd.caption}</p>
                      {renderCtaButton(false)}
                    </div>
                  </div>
                  {previewAd.mediaType === 'video' && !previewAd.m3u8Url && (
                    <p className="text-muted text-center mt-2 small">
                      Add an m3u8 URL for HLS reel playback preview.
                    </p>
                  )}
                </Tab>
              </Tabs>
            );
          })()}
        </Modal.Body>
      </Modal>

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedAd ? 'Edit Feed Ad' : 'New Feed Ad'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Ad Set</Form.Label>
              <Form.Select
                name="adSetId"
                value={formData.adSetId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Ad Set</option>
                {Array.isArray(adSets) && adSets.map(adSet => (
                  <option key={adSet.id} value={adSet.id}>
                    {adSet.adSetName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Asset Name</Form.Label>
              <Form.Control
                type="text"
                name="assetName"
                value={formData.assetName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Caption</Form.Label>
              <Form.Control
                as="textarea"
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Media Type</Form.Label>
              <Form.Select
                name="mediaType"
                value={formData.mediaType}
                onChange={handleInputChange}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Feed Image (poster / media)</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                {formData.media && (
                  <img
                    src={formData.media}
                    alt="Media Preview"
                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                  />
                )}
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploadingField === 'media'}
                />
                {uploadingField === 'media' && (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Uploading...</span>
                  </div>
                )}
              </div>
              <Form.Control
                type="url"
                name="media"
                value={formData.media}
                onChange={handleInputChange}
                placeholder="https://cdn.example.com/poster.jpg"
                required
              />
              <Form.Text className="text-muted">
                Uploaded via <code>POST /admin/ads/presigned-url</code> or paste a CDN URL.
              </Form.Text>
            </Form.Group>
            {formData.mediaType === 'video' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Video URL (m3u8 / HLS)</Form.Label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Control
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      disabled={uploadingField === 'm3u8Url'}
                    />
                    {uploadingField === 'm3u8Url' && (
                      <div className="spinner-border spinner-border-sm" role="status" />
                    )}
                  </div>
                  <Form.Control
                    type="url"
                    name="m3u8Url"
                    value={formData.m3u8Url}
                    onChange={handleInputChange}
                    placeholder="https://cdn.example.com/ad.m3u8"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Thumbnail URL</Form.Label>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {formData.thumbnailUrl && (
                      <img
                        src={formData.thumbnailUrl}
                        alt="Thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                      />
                    )}
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      disabled={uploadingField === 'thumbnailUrl'}
                    />
                  </div>
                  <Form.Control
                    type="url"
                    name="thumbnailUrl"
                    value={formData.thumbnailUrl}
                    onChange={handleInputChange}
                    placeholder="https://cdn.example.com/poster.jpg"
                  />
                </Form.Group>
              </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Supported Placements</Form.Label>
              <Form.Text className="d-block mb-2 text-muted">
                Leave all unchecked to allow any placement on the campaign.
              </Form.Text>
              <div className="d-flex flex-wrap gap-2">
                {placementOptions.map((key) => (
                  <Form.Check
                    key={key}
                    type="checkbox"
                    id={`placement-${key}`}
                    label={FEED_PLACEMENT_LABELS[key] || key}
                    checked={(formData.supportedPlacements || []).includes(key)}
                    onChange={() => handlePlacementToggle(key)}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>CTA Text</Form.Label>
              <Form.Control
                type="text"
                name="ctaText"
                value={formData.ctaText}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>CTA URL</Form.Label>
              <Form.Control
                type="url"
                name="ctaUrl"
                value={formData.ctaUrl}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Priority</Form.Label>
              <Form.Control
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                min="1"
                required
              />
              <Form.Text className="text-muted">Lower number = higher priority in rotation.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedAd ? 'Update' : 'Create'} Feed Ad
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Ads; 